"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { ConfirmationModal } from "@/components/ConfirmationModal";
import { useCategories } from "@/hooks/useCategories";
import type { CategoryItem } from "@/types";
import { AddCategoryModal } from "./AddCategoryModal";
import { EditCategoryModal } from "./EditCategoryModal";

function buildIdsAfterDragInType(
  sortedFull: CategoryItem[],
  type: "income" | "expense",
  parentCategoryId: string | null,
  fromIndexInType: number,
  toIndexInType: number,
): string[] {
  const reorderable = sortedFull.filter((c) => c.userId !== null);
  const inType = reorderable.filter(
    (c) => c.type === type && (c.parentCategoryId ?? null) === parentCategoryId,
  );
  if (
    fromIndexInType < 0 ||
    toIndexInType < 0 ||
    fromIndexInType >= inType.length ||
    fromIndexInType === toIndexInType
  ) {
    return reorderable.map((c) => c.id);
  }

  const moved = [...inType];
  const [item] = moved.splice(fromIndexInType, 1);
  const boundedTarget = Math.min(toIndexInType, moved.length);
  moved.splice(boundedTarget, 0, item);

  let i = 0;
  return reorderable.map((c) => {
    if (c.type !== type || (c.parentCategoryId ?? null) !== parentCategoryId) {
      return c.id;
    }
    return moved[i++].id;
  });
}

/** 0 = normal ordering; higher = further down. Cash Out is always last. */
function expensePinnedBottomRank(category: CategoryItem): number {
  if (
    category.type !== "expense" ||
    category.parentCategoryId !== null
  ) {
    return 0;
  }
  const key = category.name.trim().toLowerCase();
  if (key === "other expenses") return 1;
  if (key === "cash out") return 2;
  return 0;
}

function CategoryRow(props: {
  category: CategoryItem;
  priority: number;
  isSubcategory?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onAddSubcategory?: () => void;
  reorderDisabled: boolean;
  isDragOver: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: () => void;
}) {
  const {
    category,
    priority,
    isSubcategory = false,
    onEdit,
    onDelete,
    onAddSubcategory,
    reorderDisabled,
    isDragOver,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDrop,
  } = props;

  return (
    <div
      draggable={!reorderDisabled}
      onDragStart={reorderDisabled ? undefined : onDragStart}
      onDragEnd={reorderDisabled ? undefined : onDragEnd}
      onDragOver={reorderDisabled ? undefined : onDragOver}
      onDrop={reorderDisabled ? undefined : onDrop}
      className={`flex min-h-[76px] items-center justify-between gap-4 rounded-xl border px-3.5 py-3 transition-colors ${
        isDragOver
          ? "border-blue-300 bg-blue-50/60 shadow-sm"
          : "border-slate-200 bg-white"
      } ${reorderDisabled ? "" : "cursor-grab active:cursor-grabbing hover:border-slate-300"}`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span
          className="h-3.5 w-3.5 shrink-0 rounded-full"
          style={{ backgroundColor: category.color }}
          aria-hidden
        />
        <span
          className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
            reorderDisabled
              ? "text-slate-300"
              : "text-[#45556c] hover:bg-slate-100"
          }`}
          title={
            reorderDisabled
              ? isSubcategory
                ? "Order is managed automatically for subcategories"
                : "Only custom categories can be reordered"
              : "Drag to reorder"
          }
          aria-label={
            reorderDisabled
              ? isSubcategory
                ? "Subcategory cannot be reordered here"
                : "Category cannot be reordered"
              : "Drag category to reorder"
          }
        >
          <GripVertical className="h-4 w-4" strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          <p className="truncate text-[18px] font-medium leading-6 text-[#1e293b]">
            {category.name}
          </p>
          <p className="truncate text-xs text-[#64748b]">
            {category.userId ? "Custom category" : "System category"}
            {isSubcategory ? " • Subcategory" : " • Category"} • Priority {priority}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {onAddSubcategory ? (
          <button
            type="button"
            onClick={onAddSubcategory}
            className="inline-flex items-center rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
            title="Add subcategory"
          >
            + Sub
          </button>
        ) : null}
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#475569] hover:bg-slate-100"
          title="Edit category"
          >
          <Pencil className="h-4 w-4" strokeWidth={1.75} />
        </button>
          {category.userId ? (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-rose-600 hover:bg-rose-50"
              title="Delete category"
              aria-label={`Delete ${category.name}`}
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.75} />
            </button>
          ) : null}
      </div>
    </div>
  );
}

function CategorySection(props: {
  title: string;
  buttonLabel: string;
  type: "income" | "expense";
  categories: CategoryItem[];
  isLoading: boolean;
  reorderBusy: boolean;
  draggingType: "income" | "expense" | null;
  dropTargetId: string | null;
  setDraggingId: (value: string | null) => void;
  setDraggingType: (value: "income" | "expense" | null) => void;
  setDropTargetId: (value: string | null) => void;
  onDropInType: (
    type: "income" | "expense",
    targetId?: string,
    parentCategoryId?: string | null,
  ) => Promise<void>;
  onAdd: () => void;
  onAddSubcategory: (parentCategory: CategoryItem) => void;
  onEdit: (category: CategoryItem) => void;
  onDelete: (category: CategoryItem) => void;
}) {
  const {
    title,
    buttonLabel,
    type,
    categories,
    isLoading,
    reorderBusy,
    draggingType,
    dropTargetId,
    setDraggingId,
    setDraggingType,
    setDropTargetId,
    onDropInType,
    onAdd,
    onAddSubcategory,
    onEdit,
    onDelete,
  } = props;

  const topLevelCategories = categories.filter(
    (category) => category.parentCategoryId === null,
  );
  const subcategoriesByParent = new Map<string, CategoryItem[]>();
  for (const category of categories) {
    if (!category.parentCategoryId) continue;
    const rows = subcategoriesByParent.get(category.parentCategoryId) ?? [];
    rows.push(category);
    subcategoriesByParent.set(category.parentCategoryId, rows);
  }
  for (const rows of subcategoriesByParent.values()) {
    rows.sort(
      (a, b) =>
        a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
  }

  const noopDrag = () => {};

  const [expandedSubParents, setExpandedSubParents] = useState<
    Record<string, boolean>
  >({});

  const toggleSubcategories = (parentId: string) => {
    setExpandedSubParents((prev) => ({
      ...prev,
      [parentId]: !prev[parentId],
    }));
  };

  return (
    <section className="flex min-h-0 flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-[22px] font-semibold leading-7 text-[#0f172b]">
          {title}
        </h2>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex h-9 shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-[#0f172b] hover:bg-slate-50"
        >
          <Plus className="h-4 w-4" strokeWidth={2} aria-hidden />
          {buttonLabel}
        </button>
      </div>
      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto pr-1">
        {isLoading ? (
          <div className="space-y-2.5">
            {[1, 2, 3].map((k) => (
              <div
                key={k}
                className="h-[76px] animate-pulse rounded-xl bg-slate-100"
              />
            ))}
          </div>
        ) : topLevelCategories.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-sm text-[#64748b]">
            No categories yet.
          </p>
        ) : (
          topLevelCategories.map((cat, idx) => {
            const childCategories = subcategoriesByParent.get(cat.id) ?? [];
            const subsExpanded = Boolean(expandedSubParents[cat.id]);
            const subsRegionId = `category-subs-${cat.id}`;
            return (
              <div key={cat.id} className="flex gap-2">
                <div className="flex w-9 shrink-0 justify-center pt-3">
                  {childCategories.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => toggleSubcategories(cat.id)}
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
                      aria-expanded={subsExpanded}
                      aria-controls={subsRegionId}
                      title={
                        subsExpanded
                          ? "Hide subcategories"
                          : `Show ${childCategories.length} subcategor${childCategories.length === 1 ? "y" : "ies"}`
                      }
                    >
                      {subsExpanded ? (
                        <ChevronDown className="h-5 w-5" strokeWidth={2} aria-hidden />
                      ) : (
                        <ChevronRight className="h-5 w-5" strokeWidth={2} aria-hidden />
                      )}
                    </button>
                  ) : (
                    <span className="block h-9 w-9 shrink-0" aria-hidden />
                  )}
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <CategoryRow
                    category={cat}
                    priority={idx + 1}
                    onAddSubcategory={() => onAddSubcategory(cat)}
                    reorderDisabled={reorderBusy || cat.userId === null}
                    isDragOver={dropTargetId === cat.id}
                    onDragStart={() => {
                      setDraggingId(cat.id);
                      setDraggingType(type);
                      setDropTargetId(cat.id);
                    }}
                    onDragEnd={() => {
                      setDraggingId(null);
                      setDraggingType(null);
                      setDropTargetId(null);
                    }}
                    onDragOver={(event) => {
                      event.preventDefault();
                      if (dropTargetId !== cat.id) {
                        setDropTargetId(cat.id);
                      }
                    }}
                    onDrop={() => {
                      void onDropInType(type, cat.id, null);
                      setDraggingId(null);
                      setDraggingType(null);
                      setDropTargetId(null);
                    }}
                    onEdit={() => onEdit(cat)}
                    onDelete={() => onDelete(cat)}
                  />
                  {childCategories.length > 0 && subsExpanded ? (
                    <div
                      id={subsRegionId}
                      className="space-y-2 border-l border-slate-200 pl-4"
                      role="region"
                      aria-label={`Subcategories of ${cat.name}`}
                    >
                      {childCategories.map((subCategory, subIdx) => (
                        <CategoryRow
                          key={subCategory.id}
                          category={subCategory}
                          priority={subIdx + 1}
                          isSubcategory
                          reorderDisabled
                          isDragOver={false}
                          onDragStart={noopDrag}
                          onDragEnd={noopDrag}
                          onDragOver={noopDrag}
                          onDrop={noopDrag}
                          onEdit={() => onEdit(subCategory)}
                          onDelete={() => onDelete(subCategory)}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
        {draggingType === type ? (
          <div
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => {
              void onDropInType(type, undefined, null);
              setDraggingId(null);
              setDraggingType(null);
              setDropTargetId(null);
            }}
            className="h-8 rounded-[10px] border border-dashed border-blue-200 bg-blue-50/40"
          />
        ) : null}
      </div>
    </section>
  );
}

export default function CategoryManagerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [editCategory, setEditCategory] = useState<CategoryItem | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState<"income" | "expense">("expense");
  const [reorderBusy, setReorderBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CategoryItem | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [draggingType, setDraggingType] = useState<"income" | "expense" | null>(
    null,
  );
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const reorderInFlightRef = useRef(false);
  const [addParentId, setAddParentId] = useState<string | null | undefined>(
    undefined,
  );
  const showBackToCashflow =
    (location.state as { from?: string } | null)?.from === "/cashflow";

  const {
    categories,
    error: catError,
    isLoading,
    mutate,
    createCategory,
    reorderCategories,
    deleteCategory,
  } = useCategories();

  const sorted = useMemo(() => {
    const withIdx = categories.map((c, i) => ({ c, i }));
    withIdx.sort((a, b) => {
      const aPin = expensePinnedBottomRank(a.c);
      const bPin = expensePinnedBottomRank(b.c);
      if (aPin !== bPin) {
        if (aPin === 0) return -1;
        if (bPin === 0) return 1;
        return aPin - bPin;
      }
      return a.i - b.i;
    });
    return withIdx.map((x) => x.c);
  }, [categories]);

  const incomeCats = sorted.filter((c) => c.type === "income");
  const expenseCats = sorted.filter((c) => c.type === "expense");

  const applyReorder = useCallback(
    async (orderedIds: string[]) => {
      if (reorderInFlightRef.current) return;
      const currentOrder = sorted
        .filter((c) => c.userId !== null)
        .map((c) => c.id);
      const unchanged =
        orderedIds.length === currentOrder.length &&
        orderedIds.every((id, idx) => id === currentOrder[idx]);
      if (unchanged) return;
      reorderInFlightRef.current = true;
      setReorderBusy(true);
      try {
        await reorderCategories(orderedIds);
      } finally {
        reorderInFlightRef.current = false;
        setReorderBusy(false);
      }
    },
    [reorderCategories, sorted],
  );

  const handleDropInType = useCallback(
    async (
      type: "income" | "expense",
      targetId?: string,
      parentCategoryId: string | null = null,
    ) => {
      if (!draggingId) return;
      const typeRows = sorted.filter(
        (c) =>
          c.type === type &&
          c.userId !== null &&
          (c.parentCategoryId ?? null) === parentCategoryId,
      );
      const fromIndex = typeRows.findIndex((c) => c.id === draggingId);
      const toIndex = targetId
        ? typeRows.findIndex((c) => c.id === targetId)
        : typeRows.length;
      if (fromIndex === -1 || toIndex === -1) return;
      await applyReorder(
        buildIdsAfterDragInType(
          sorted,
          type,
          parentCategoryId,
          fromIndex,
          toIndex,
        ),
      );
    },
    [applyReorder, draggingId, sorted],
  );

  function openEdit(cat: CategoryItem) {
    setEditCategory(cat);
    setEditOpen(true);
  }

  async function confirmDeleteCategory() {
    if (!deleteTarget?.userId) return;
    setDeleteBusy(true);
    try {
      await deleteCategory(deleteTarget.id);
      setDeleteTarget(null);
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <div className="mx-auto flex h-full max-h-screen w-full min-h-0 flex-col gap-4 overflow-hidden px-6 pb-8 pt-5">
      {showBackToCashflow ? (
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex w-fit items-center gap-1.5 rounded-md px-1 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to cashflow
        </button>
      ) : null}

      <header className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-[34px] font-semibold leading-10 text-[#0f172b]">
              Categories
            </h1>
            <p className="text-[17px] leading-6 text-[#64748b]">
              Organize and manage your transaction categories
            </p>
          </div>
        </div>
      </header>

      {catError ? (
        <p className="text-sm text-red-600">Could not load categories.</p>
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 xl:grid-cols-2">
        <CategorySection
          title="Cash in "
          buttonLabel="Add Category"
          type="income"
          categories={incomeCats}
          isLoading={isLoading}
          reorderBusy={reorderBusy}
          draggingType={draggingType}
          dropTargetId={dropTargetId}
          setDraggingId={setDraggingId}
          setDraggingType={setDraggingType}
          setDropTargetId={setDropTargetId}
          onDropInType={handleDropInType}
          onAdd={() => {
            setAddType("income");
            setAddParentId(undefined);
            setAddOpen(true);
          }}
          onAddSubcategory={(parentCategory) => {
            setAddType(parentCategory.type === "income" ? "income" : "expense");
            setAddParentId(parentCategory.id);
            setAddOpen(true);
          }}
          onEdit={openEdit}
          onDelete={setDeleteTarget}
        />
        <CategorySection
          title="Cash out"
          buttonLabel="Add Cash out Category"
          type="expense"
          categories={expenseCats}
          isLoading={isLoading}
          reorderBusy={reorderBusy}
          draggingType={draggingType}
          dropTargetId={dropTargetId}
          setDraggingId={setDraggingId}
          setDraggingType={setDraggingType}
          setDropTargetId={setDropTargetId}
          onDropInType={handleDropInType}
          onAdd={() => {
            setAddType("expense");
            setAddParentId(undefined);
            setAddOpen(true);
          }}
          onAddSubcategory={(parentCategory) => {
            setAddType(parentCategory.type === "income" ? "income" : "expense");
            setAddParentId(parentCategory.id);
            setAddOpen(true);
          }}
          onEdit={openEdit}
          onDelete={setDeleteTarget}
        />
      </div>

      <AddCategoryModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setAddParentId(undefined);
        }}
        presetType={addType}
        parentId={addParentId}
        categories={sorted}
        onCreate={(name, color, type, parentCategoryId) =>
          createCategory(name, color, type, parentCategoryId)
        }
      />

      <EditCategoryModal
        category={editCategory}
        categories={sorted}
        hasChildren={
          editCategory
            ? sorted.some((category) => category.parentCategoryId === editCategory.id)
            : false
        }
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditCategory(null);
        }}
        onSaved={() => void mutate()}
      />

      <ConfirmationModal
        open={Boolean(deleteTarget)}
        title={
          deleteTarget ? `Delete "${deleteTarget.name}"?` : "Delete category?"
        }
        description="This will also delete all keywords in this category and unassign this category from existing transactions."
        confirmLabel="Delete"
        confirmTone="danger"
        isLoading={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setDeleteTarget(null);
        }}
        onConfirm={() => void confirmDeleteCategory()}
      />
    </div>
  );
}
